import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { postPolicyApplicationFormSubmissionRequest } from '@client/model';
import { getPolicyUpdatesForCarrier } from '@shared/policy-application/pre-fill-utils/pre-fill-util';
import { fundingPolicyTypeDefs } from '@shared';

interface UseApplyPolicyUpdatesToDatabaseParams {
  client: Client;
  policyApplication: ClientPolicyApplication;
  clientPolicyApplicationId: string;
}

export const useApplyPolicyUpdatesToDatabase = ({
  client,
  policyApplication,
  clientPolicyApplicationId,
}: UseApplyPolicyUpdatesToDatabaseParams) => {
  const dispatch = useDispatch();

  const applyPolicyUpdatesToDatabase = useCallback(() => {
    // Find the application-form step
    const applicationStep = policyApplication.steps?.find(
      step => step.stepType === 'application-form',
    );

    if (
      !applicationStep?.clientPolicyApplicationForm ||
      !applicationStep.policyApplicationFormId
    ) {
      return { success: false, message: 'Application form not found' };
    }

    const currentSubmissionData =
      applicationStep.clientPolicyApplicationForm.submissionData || {};
    const fundingSourceId = policyApplication.fundingSourceId;

    // Get policyFundingSource from client
    const policyFundingSource =
      fundingSourceId && client.multipleFundingSources?.ltcPolicy
        ? client.multipleFundingSources.ltcPolicy[fundingSourceId]
        : null;

    if (!policyFundingSource) {
      return { success: false, message: 'Policy funding source not found' };
    }

    // Get carrier from funding source
    const fundingSourceDef = policyApplication.fundingPolicyType
      ? fundingPolicyTypeDefs[policyApplication.fundingPolicyType]
      : null;
    const carrier = fundingSourceDef?.carrier || '';

    if (!carrier) {
      return { success: false, message: 'Carrier not found' };
    }

    // Get policy updates
    const updates = getPolicyUpdatesForCarrier(carrier, policyFundingSource);

    if (updates.length === 0) {
      return { success: false, message: 'No policy updates available' };
    }

    // Merge updates into current submission data
    const updatedSubmissionData = {
      ...currentSubmissionData,
      ...updates.reduce(
        (acc, update) => {
          acc[update.questionRef] = update.answer;
          return acc;
        },
        {} as Record<string, PolicyFormSubmissionDataValue>,
      ),
    };

    // Save to database
    dispatch(
      postPolicyApplicationFormSubmissionRequest({
        clientId: client.clientId,
        forClientId: applicationStep.clientPolicyApplicationForm.forClientId,
        clientPolicyApplicationId,
        policyApplicationFormId: applicationStep.policyApplicationFormId,
        submissionData: updatedSubmissionData,
        status: applicationStep.clientPolicyApplicationForm.status,
        visitedSubsections:
          applicationStep.clientPolicyApplicationForm.visitedSubsections || [],
      }),
    );

    return { success: true, updatesCount: updates.length };
  }, [client, policyApplication, clientPolicyApplicationId, dispatch]);

  return { applyPolicyUpdatesToDatabase };
};

